import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "../lib/use-auth";
import { hasPermission } from "../lib/permissions";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Modal } from "../components/ui/Modal/Modal";
import { Button } from "../components/ui/Button/Button";
import {
	useCompanyList,
	useCreateCompany,
	useSapCurrencies,
	useSapBpGroups,
	useSapBpSeries,
	useSapStates,
	useSapCountries,
	useSalesPersons,
	useAssignableUsers,
	useUpdateCompany,
	type CreateCompanyInput,
	type Company,
} from "../hooks/use-companies";
import { FormField } from "../components/ui/FormField/FormField";
import { Select } from "../components/ui/Select/Select";
import styles from "./Companies.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/companies",
	component: withPermission(CompaniesPage, "companies", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
	}),
});

export type CompanyStatus = "lead" | "customer" | "vendor" | "inactive";

export const EMPTY_FORM = {
	legalName: "",
	tradeName: "",
	status: "lead" as CompanyStatus,
	sapSeries: undefined as number | undefined,
	sapGroupCode: undefined as number | undefined,
	currency: "",
	currencies: [] as string[],
	taxNumber: "",
	taxOffice: "",
	email: "",
	phone: "",
	fax: "",
	website: "",
	industry: "",
	notes: "",
	ownerId: "",
	addressStreet: "",
	addressCity: "",
	addressState: "",
	addressPostalCode: "",
	addressCountry: "Türkiye",
};

export type CompanyFormValues = typeof EMPTY_FORM;

export function CreateCompanyModal({
	isOpen,
	onClose,
	initialValues,
	title,
	submitLabel,
	onSubmit,
	convertMode = false,
}: {
	isOpen: boolean;
	onClose: () => void;
	initialValues?: Partial<CompanyFormValues>;
	title?: string;
	submitLabel?: string;
	onSubmit?: (values: CreateCompanyInput) => Promise<void>;
	convertMode?: boolean;
}) {
	const { t } = useTranslation();

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title ?? t("createCompany")}
			closeOnOverlayClick={false}
		>
			{isOpen && (
				<CreateCompanyForm
					onClose={onClose}
					initialValues={initialValues}
					submitLabel={submitLabel}
					onSubmit={onSubmit}
					convertMode={convertMode}
				/>
			)}
		</Modal>
	);
}

const CUSTOMER_SERIES_PATTERN = /mü[sş]/i;
const ALLOWED_CURRENCY_CODES = new Set(["EUR", "USD", "TRY", "TL"]);

function CreateCompanyForm({
	onClose,
	initialValues,
	submitLabel,
	onSubmit,
	convertMode = false,
}: {
	onClose: () => void;
	initialValues?: Partial<CompanyFormValues> | undefined;
	submitLabel?: string | undefined;
	onSubmit?: ((values: CreateCompanyInput) => Promise<void>) | undefined;
	convertMode?: boolean;
}) {
	const { t } = useTranslation();
	const createCompany = useCreateCompany();
	const { user } = useAuth();
	const isAdmin = user?.role === "admin";
	const { data: allCurrencies } = useSapCurrencies();
	const currencies = allCurrencies?.filter((c) => ALLOWED_CURRENCY_CODES.has(c.code.toUpperCase()));
	const { data: bpGroups } = useSapBpGroups();
	const { data: bpSeries } = useSapBpSeries();
	const { data: countries } = useSapCountries();
	const { data: salesPersons } = useSalesPersons();
	const [form, setForm] = useState<CompanyFormValues>(() => ({
		...EMPTY_FORM,
		...(!isAdmin && user?.id ? { ownerId: user.id } : {}),
		...initialValues,
	}));
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const selectedCountryCode = countries?.find((c) => c.name === form.addressCountry)?.code;
	const { data: states } = useSapStates(selectedCountryCode);

	const seriesOptions = convertMode
		? bpSeries?.filter((s) => CUSTOMER_SERIES_PATTERN.test(s.name))
		: bpSeries;
	const selectedSeries = bpSeries?.find((s) => s.series === form.sapSeries);
	const nextCodePreview = selectedSeries?.nextCode ?? null;

	const ownSalesPerson = salesPersons?.find((sp) => sp.id === user?.id);

	const set = (field: keyof typeof EMPTY_FORM, value: string | number | undefined) =>
		setForm((prev) => ({ ...prev, [field]: value }));

	const isForeignAddress = !!form.addressCountry && form.addressCountry !== "Türkiye";

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		const isMissing =
			!form.legalName.trim() ||
			!form.tradeName.trim() ||
			form.sapSeries === undefined ||
			!form.status ||
			form.sapGroupCode === undefined ||
			form.currencies.length === 0 ||
			!form.ownerId.trim() ||
			(!isForeignAddress && !form.taxNumber.trim()) ||
			(!isForeignAddress && !form.taxOffice.trim()) ||
			!form.phone.trim() ||
			!form.email.trim();
		if (isMissing) {
			setError(t("requiredField"));
			return;
		}
		const {
			addressStreet,
			addressCity,
			addressState,
			addressPostalCode,
			addressCountry,
			...formRest
		} = form;
		const payload = {
			...formRest,
			legalName: form.legalName.trim(),
			tradeName: form.tradeName?.trim() || undefined,
			sapSeries: form.sapSeries ?? undefined,
			currency: form.currencies[0] || form.currency || undefined,
			currencies: form.currencies.length > 0 ? form.currencies : undefined,
			taxNumber: form.taxNumber?.trim() || (isForeignAddress ? "2222222222" : undefined),
			taxOffice: form.taxOffice?.trim() || undefined,
			email: form.email?.trim() || undefined,
			phone: form.phone?.trim() || undefined,
			fax: form.fax?.trim() || undefined,
			website: form.website?.trim() || undefined,
			industry: form.industry?.trim() || undefined,
			notes: form.notes?.trim() || undefined,
			ownerId: form.ownerId || undefined,
			address:
				addressStreet || addressCity || addressState || addressPostalCode
					? {
							street: addressStreet?.trim() || undefined,
							city: addressCity?.trim() || undefined,
							state: addressState?.trim() || undefined,
							postalCode: addressPostalCode?.trim() || undefined,
							country: addressCountry?.trim() || undefined,
						}
					: undefined,
		};
		setIsSubmitting(true);
		try {
			if (onSubmit) {
				await onSubmit(payload);
			} else {
				await createCompany.mutateAsync(payload);
			}
			setForm(EMPTY_FORM);
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : t("createFailed"));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleClose = () => {
		setForm(EMPTY_FORM);
		setError(null);
		onClose();
	};

	return (
		<form className={styles.form} onSubmit={handleSubmit}>
			<div className={styles.formGrid}>
				<div className={styles.formGroup}>
					<label>
						{t("legalName")}
						<span className={styles.required}>*</span>
					</label>
					<input
						type="text"
						value={form.legalName}
						onChange={(e) => set("legalName", e.target.value)}
						autoFocus
					/>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("tradeName")}
						<span className={styles.required}>*</span>
					</label>
					<input
						type="text"
						value={form.tradeName ?? ""}
						onChange={(e) => set("tradeName", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("sapSeries")}
						<span className={styles.required}>*</span>
					</label>
					<select
						value={form.sapSeries ?? ""}
						onChange={(e) => set("sapSeries", e.target.value ? Number(e.target.value) : undefined)}
					>
						<option value="">{t("notAvailable")}</option>
						{seriesOptions && seriesOptions.length > 0
							? seriesOptions.map((s) => (
									<option key={s.series} value={s.series}>
										{s.name}
									</option>
								))
							: null}
					</select>
					{nextCodePreview && (
						<span
							style={{
								fontSize: "0.8125rem",
								color: "var(--color-fg-muted)",
								marginTop: 2,
							}}
						>
							{t("nextCode")}: <strong>{nextCodePreview}</strong>
						</span>
					)}
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("status")}
						<span className={styles.required}>*</span>
					</label>
					<select
						value={convertMode ? "customer" : form.status}
						disabled={convertMode}
						onChange={(e) => set("status", e.target.value as CompanyStatus)}
					>
						{convertMode ? (
							<option value="customer">{t("customer")}</option>
						) : (
							<>
								<option value="lead">{t("lead")}</option>
								<option value="customer">{t("customer")}</option>
								<option value="vendor">{t("vendor")}</option>
								<option value="inactive">{t("inactive")}</option>
							</>
						)}
					</select>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("group")}
						<span className={styles.required}>*</span>
					</label>
					<select
						value={form.sapGroupCode ?? ""}
						onChange={(e) =>
							set("sapGroupCode", e.target.value ? Number(e.target.value) : undefined)
						}
					>
						<option value="">{t("notAvailable")}</option>
						{bpGroups && bpGroups.length > 0
							? bpGroups.map((g) => (
									<option key={g.code} value={g.code}>
										{g.name}
									</option>
								))
							: null}
					</select>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("currency")}
						<span className={styles.required}>*</span>
					</label>
					<div className={styles.currencyList}>
						{currencies && currencies.length > 0 ? (
							currencies.map((c) => {
								const checked = form.currencies.includes(c.code);
								return (
									<label key={c.code} className={styles.currencyOption}>
										<input
											type="checkbox"
											checked={checked}
											onChange={() =>
												setForm((prev) => {
													const picked = checked
														? prev.currencies.filter((x) => x !== c.code)
														: [...prev.currencies, c.code];
													return { ...prev, currencies: picked, currency: picked[0] ?? "" };
												})
											}
										/>
										{c.name} ({c.code})
									</label>
								);
							})
						) : (
							<span className={styles.currencyEmpty}>{t("notAvailable")}</span>
						)}
					</div>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("salesPerson")}
						<span className={styles.required}>*</span>
					</label>
					<select
						value={form.ownerId ?? ""}
						onChange={(e) => set("ownerId", e.target.value)}
						disabled={!isAdmin}
					>
						{isAdmin ? (
							<>
								<option value="">{t("notAvailable")}</option>
								{salesPersons && salesPersons.length > 0
									? salesPersons.map((sp) => (
											<option key={sp.id} value={sp.id}>
												{sp.name}
											</option>
										))
									: null}
							</>
						) : (
							<option value={user?.id ?? ""}>{ownSalesPerson?.name ?? user?.email ?? ""}</option>
						)}
					</select>
				</div>
				<div className={styles.formGroup} style={{ gridColumn: "span 2" }}>
					<label>Adres</label>
					<input
						type="text"
						value={form.addressStreet ?? ""}
						onChange={(e) => set("addressStreet", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>Posta Kodu</label>
					<input
						type="text"
						value={form.addressPostalCode ?? ""}
						onChange={(e) => set("addressPostalCode", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>İlçe</label>
					<input
						type="text"
						value={form.addressCity ?? ""}
						onChange={(e) => set("addressCity", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>İl</label>
					<select
						value={form.addressState ?? ""}
						onChange={(e) => set("addressState", e.target.value)}
					>
						<option value="">{t("notAvailable")}</option>
						{(states ?? []).map((s) => (
							<option key={s.code} value={s.name}>
								{s.name}
							</option>
						))}
					</select>
				</div>
				<div className={styles.formGroup}>
					<label>Ülke</label>
					<select
						value={form.addressCountry ?? ""}
						onChange={(e) => {
							const country = e.target.value;
							set("addressCountry", country);
							set("addressState", undefined);
							if (country && country !== "Türkiye") {
								if (!form.taxNumber.trim()) set("taxNumber", "2222222222");
							} else if (form.taxNumber === "2222222222") {
								set("taxNumber", "");
							}
						}}
					>
						<option value="">{t("notAvailable")}</option>
						{(countries ?? []).map((c) => (
							<option key={c.code} value={c.name}>
								{c.name}
							</option>
						))}
					</select>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("taxNumber")}
						{!isForeignAddress && <span className={styles.required}>*</span>}
					</label>
					<input
						type="text"
						value={form.taxNumber ?? ""}
						onChange={(e) => set("taxNumber", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("taxOffice")}
						{!isForeignAddress && <span className={styles.required}>*</span>}
					</label>
					<input
						type="text"
						value={form.taxOffice ?? ""}
						onChange={(e) => set("taxOffice", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("phone")}
						<span className={styles.required}>*</span>
					</label>
					<input
						type="text"
						value={form.phone ?? ""}
						onChange={(e) => set("phone", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>{t("fax")}</label>
					<input type="text" value={form.fax ?? ""} onChange={(e) => set("fax", e.target.value)} />
				</div>
				<div className={styles.formGroup}>
					<label>
						{t("email")}
						<span className={styles.required}>*</span>
					</label>
					<input
						type="email"
						value={form.email ?? ""}
						onChange={(e) => set("email", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup}>
					<label>{t("industry")}</label>
					<input
						type="text"
						value={form.industry ?? ""}
						onChange={(e) => set("industry", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup} style={{ gridColumn: "span 2" }}>
					<label>{t("website")}</label>
					<input
						type="text"
						value={form.website ?? ""}
						onChange={(e) => set("website", e.target.value)}
					/>
				</div>
				<div className={styles.formGroup} style={{ gridColumn: "span 2" }}>
					<label>{t("notes")}</label>
					<textarea
						value={form.notes ?? ""}
						onChange={(e) => set("notes", e.target.value)}
						rows={3}
						style={{
							padding: "var(--space-2) var(--space-3)",
							border: "1px solid var(--color-border)",
							borderRadius: "var(--radius-md)",
							background: "var(--color-bg)",
							color: "var(--color-fg)",
							fontSize: "0.875rem",
							width: "100%",
							resize: "vertical",
						}}
					/>
				</div>
			</div>
			{error && <p style={{ color: "var(--color-danger)", fontSize: "0.875rem" }}>{error}</p>}
			<div className={styles.formActions}>
				<Button type="button" variant="secondary" onClick={handleClose}>
					{t("cancel")}
				</Button>
				<Button type="submit" isLoading={onSubmit ? isSubmitting : createCompany.isPending}>
					{submitLabel ?? t("create")}
				</Button>
			</div>
		</form>
	);
}

function AssignCompanyModal({
	companyId,
	currentOwnerId,
	onClose,
}: {
	companyId: string | null;
	currentOwnerId: string | null;
	onClose: () => void;
}) {
	const { t } = useTranslation();

	return (
		<Modal isOpen={!!companyId} onClose={onClose} title={t("assignSalesPerson")}>
			{companyId && (
				<AssignCompanyForm
					companyId={companyId}
					currentOwnerId={currentOwnerId}
					onClose={onClose}
				/>
			)}
		</Modal>
	);
}

function AssignCompanyForm({
	companyId,
	currentOwnerId,
	onClose,
}: {
	companyId: string;
	currentOwnerId: string | null;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data: assignableUsers } = useAssignableUsers();
	const updateCompany = useUpdateCompany();
	const [selectedUserId, setSelectedUserId] = useState(currentOwnerId ?? "");
	const [error, setError] = useState<string | null>(null);

	const handleSave = async () => {
		if (!selectedUserId) return;
		setError(null);
		try {
			await updateCompany.mutateAsync({ id: companyId, data: { ownerId: selectedUserId } });
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : t("updateFailed"));
		}
	};

	return (
		<>
			<FormField label={t("salesPerson")}>
				<Select
					options={[
						{ value: "", label: t("selectOption") },
						...(assignableUsers ?? []).map((u) => ({ value: u.id, label: u.name })),
					]}
					value={selectedUserId}
					onChange={(e) => setSelectedUserId(e.target.value)}
				/>
			</FormField>
			{error && <p style={{ color: "var(--color-danger)", fontSize: "0.875rem" }}>{error}</p>}
			<div className={styles.formActions}>
				<Button type="button" variant="secondary" onClick={onClose}>
					{t("cancel")}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					isLoading={updateCompany.isPending}
					disabled={!selectedUserId}
				>
					{t("save")}
				</Button>
			</div>
		</>
	);
}

function CompaniesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate({ from: "/companies" });
	const { user } = useAuth();
	const canCreate = user?.role === "admin";
	const canUpdate = hasPermission(user, "companies", "update");
	const { page, size, status, search, createdAtFrom, createdAtTo } = route.useSearch();
	const [modalOpen, setModalOpen] = useState(false);
	const [assignCompany, setAssignCompany] = useState<{
		id: string;
		ownerId: string | null;
	} | null>(null);
	const setStatus = (s: string) =>
		navigate({ search: (prev) => ({ ...prev, status: s, page: 1 }) });

	const setPage = (p: number) => navigate({ search: (prev) => ({ ...prev, page: p }) });
	const setPageSize = (s: number) =>
		navigate({ search: (prev) => ({ ...prev, size: s, page: 1 }) });
	const { data, isLoading, isError, refetch } = useCompanyList({
		status: status || undefined,
		search: search || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		page,
		limit: size,
	});

	const companies = data?.items ?? [];
	const total = data?.total ?? 0;

	const tabs = [
		{ key: "", label: t("all") },
		{ key: "customer", label: t("customer") },
		{ key: "lead", label: t("lead") },
	];

	return (
		<div>
			<div className={styles.headerRow}>
				<PageHeader title={t("companies")} />
				{canCreate && (
					<Button onClick={() => setModalOpen(true)}>
						<Plus size={16} style={{ marginRight: 6 }} />
						{t("createCompany")}
					</Button>
				)}
			</div>

			<div className={styles.filters}>
				<div className={styles.tabs}>
					{tabs.map((tab) => (
						<button
							type="button"
							key={tab.key}
							className={[styles.tab, status === tab.key && styles.active]
								.filter(Boolean)
								.join(" ")}
							onClick={() => {
								setStatus(tab.key);
								setPage(1);
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
				<FilterBar
					filters={[
						{ key: "search", type: "text", label: t("search") },
						{ key: "createdAt", type: "dateRange", label: t("createdAt") },
					]}
					values={{ search, createdAtFrom, createdAtTo }}
					onChange={(vals) => {
						navigate({
							search: (prev) => ({
								...prev,
								search: vals.search || "",
								createdAtFrom: vals.createdAtFrom || "",
								createdAtTo: vals.createdAtTo || "",
								page: 1,
							}),
						});
					}}
				/>
			</div>

			<Card>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						columns={[
							{ key: "legalName", header: t("legalName") },
							{ key: "tradeName", header: t("tradeName") },
							{ key: "taxNumber", header: t("taxNumber") },
							{
								key: "status",
								header: t("status"),
								render: (row) => <StatusBadge status={row.status} type="generic" />,
							},
							{ key: "sapCardCode", header: t("sapCardCode") },
							...(canUpdate
								? [
										{
											key: "assign",
											header: t("salesPerson"),
											render: (row: Company) => (
												<div className={styles.ownerCell}>
													<span>
														{row.owner
															? [row.owner.firstName, row.owner.lastName].filter(Boolean).join(" ")
															: "—"}
													</span>
													<button
														type="button"
														className={styles.assignButton}
														onClick={() => setAssignCompany({ id: row.id, ownerId: row.ownerId })}
													>
														{t("assign")}
													</button>
												</div>
											),
										},
									]
								: []),
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/companies/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={companies}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={size}
					total={total}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
				/>
			</Card>

			<CreateCompanyModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
			<AssignCompanyModal
				companyId={assignCompany?.id ?? null}
				currentOwnerId={assignCompany?.ownerId ?? null}
				onClose={() => setAssignCompany(null)}
			/>
		</div>
	);
}
